import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQueries } from "@tanstack/react-query";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatCard } from "blocks-nextjs/blocks-react/components/Card/Card";
import { CatContextualMenu } from "blocks-nextjs/blocks-react/components/ContextualMenu/ContextualMenu";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconArrowDownAlt } from "blocks-nextjs/blocks-react/components/Icons/ArrowDownAlt/ArrowDownAlt";
import { CatIconArrowUpAlt } from "blocks-nextjs/blocks-react/components/Icons/ArrowUpAlt/ArrowUpAlt";
import { CatIconChevronDown } from "blocks-nextjs/blocks-react/components/Icons/ChevronDown/ChevronDown";
import { CatIconChevronUp } from "blocks-nextjs/blocks-react/components/Icons/ChevronUp/ChevronUp";
import { CatIconWarningTriangleFilled } from "blocks-nextjs/blocks-react/components/Icons/WarningTriangleFilled/WarningTriangleFilled";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistoricalDateRange } from "../../hooks/useHistoricalDateRange";
import Alerts from "../../pages/Projects/Alerts.component";
import EssHistoricalEmptyState from "../../pages/Projects/EssHistoricalEmptyState";
import { getCESTLocalTime } from "../../pages/Projects/ProjectDetails.helper";
import { getProjectAssetChargeActivitiesDataWithAssetId } from "../../services/api/project-asset-details.service";
import { groupStatusHistory, useMethanolStatisticsQuery } from "../../services/query/methanol-statistics.query";
import { getTimezoneAbbr } from "../../shared/helper";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import DateRangeDropdown from "../DateRangeDropdown/DateRangeDropdown";
import CustomDatePickerPopover from "../GenerateLink/CustomDatePickerPopover";
import EssChargeHistoryTotal from "./EssChargeHistoryTotal";
const TIMELINE_INITIAL_COUNT = 3;
const MAX_CUSTOM_DATE_RANGE_DAYS = 30;
const TIMESTAMP_FORMAT = "MM/dd/yyyy; HH:mm";
const MISSING_VALUE = "--";
const MINUTES_PER_HOUR = 60;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_ACTIVITY_RANGE_DAYS = 31;
const ACTIVITY_STALE_TIME_MS = 5 * 60 * 1000;
const CARET_ICON_SIZE_STYLE = { "--cat-icon-height": "24px", "--cat-icon-width": "24px" };
/**
 * Asset-status categories for the historical breakdown donut. Keys match the
 * normalised buckets returned by {@link groupStatusHistory}; colours mirror the
 * legacy ESS "Activity History" donut for feature parity.
 */
const ESS_STATUS_CONFIG = {
    charging: {
        color: "#1AC69E",
        label: "Charging",
        tooltip: "Total time the asset spent charging (drawing energy to raise its State of Charge) over the selected range."
    },
    discharging: {
        color: "#FB5A00",
        label: "Discharging",
        tooltip: "Total time the asset spent discharging (delivering energy and lowering its State of Charge) over the selected range."
    },
    idle: {
        color: "#0066FF",
        label: "Idle",
        tooltip: "Total time the asset was connected but neither charging nor discharging over the selected range."
    },
    notreporting: { color: "#777777", label: "Not Reported" }
};
/** Expand a from/to ISO range into a capped list of yyyy-MM-dd day strings (one query per day). */
const toDayList = (fromDate, toDate) => {
    if (!fromDate || !toDate)
        return [];
    // fromDate/toDate are project-offset ISO strings (e.g. "2026-07-01T00:00:00.000+01:00").
    // Take their leading calendar-date portion and iterate over UTC-anchored midnights so the
    // day list follows the project's calendar days and never shifts by ±1 with the browser's
    // local timezone.
    const start = new Date(`${fromDate.slice(0, 10)}T00:00:00.000Z`);
    const end = new Date(`${toDate.slice(0, 10)}T00:00:00.000Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
        return [];
    const days = [];
    for (let time = start.getTime(); time <= end.getTime() && days.length < MAX_ACTIVITY_RANGE_DAYS; time += MS_PER_DAY) {
        days.push(new Date(time).toISOString().slice(0, 10));
    }
    return days;
};
/** Fractional hours between two ISO timestamps (0 when missing/invalid/negative). */
const hoursBetween = (start, end) => {
    if (!start || !end)
        return 0;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    return Number.isFinite(ms) && ms > 0 ? ms / (MINUTES_PER_HOUR * MINUTES_PER_HOUR * 1000) : 0;
};
/** Format a project-local timestamp with the project timezone abbreviation appended. */
const formatEventTimestamp = (timestamp, projectTimezone) => {
    if (!timestamp)
        return MISSING_VALUE;
    const formatted = getCESTLocalTime(timestamp, TIMESTAMP_FORMAT);
    if (!formatted)
        return MISSING_VALUE;
    const tzAbbr = getTimezoneAbbr(projectTimezone || "UTC", timestamp);
    return tzAbbr ? `${formatted} ${tzAbbr}` : formatted;
};
/** Render a State-of-Charge value as an " at 82%" suffix, or an empty string when absent. */
const formatSocSuffix = (soc) => typeof soc === "number" && Number.isFinite(soc) ? ` at ${Math.round(soc)}%` : "";
/**
 * Map a raw activity/asset-status string to the same normalised buckets used by
 * {@link groupStatusHistory} so Timeline rows stay consistent with the breakdown
 * donut (e.g. "AC_Charging"/"DC_Charging" → "charging", "Working" → "discharging").
 */
const normaliseStatusKey = (raw) => {
    const status = (raw || "").toLowerCase().replace(/\s+/g, "");
    if (status === "charging" || status === "ac_charging" || status === "dc_charging")
        return "charging";
    if (status === "idle")
        return "idle";
    if (status === "discharging" || status === "working")
        return "discharging";
    return "notreporting";
};
/** Format a count with a singular/plural unit label (e.g. "1 Hour", "15 minutes"). */
const pluralize = (count, singular) => {
    const unit = count === 1 ? singular : `${singular}s`;
    return `${count} ${unit}`;
};
/** Format a duration in hours as "X Hours Y minutes" (designer wording), or the
 *  missing-value marker when absent. */
const formatDuration = (hours) => {
    if (typeof hours !== "number" || !Number.isFinite(hours) || hours <= 0)
        return MISSING_VALUE;
    let wholeHours = Math.floor(hours);
    let minutes = Math.round((hours - wholeHours) * MINUTES_PER_HOUR);
    if (minutes === MINUTES_PER_HOUR) {
        wholeHours += 1;
        minutes = 0;
    }
    const hoursPart = wholeHours > 0 ? pluralize(wholeHours, "Hour") : "";
    const minutesPart = minutes > 0 ? pluralize(minutes, "minute") : "";
    if (hoursPart && minutesPart)
        return `${hoursPart} ${minutesPart}`;
    return hoursPart || minutesPart || MISSING_VALUE;
};
/** Format an energy total to two decimals with locale grouping, matching the designer
 *  presentation (e.g. "380.56" / "1,380.56"). */
const formatEnergyValue = (value) => value.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
/** Aggregate per-segment charge/discharge energy (kWh) and session counts from the
 *  activity segments. Each charge/discharge segment counts as one session; energy is
 *  summed from the per-segment chargeEnergy/dischargeEnergy fields. */
const deriveActivityEnergyTotals = (segments) => {
    let activityChargeEnergy = 0;
    let activityDischargeEnergy = 0;
    let chargingSessions = 0;
    let dischargingSessions = 0;
    let hasActivityEnergy = false;
    segments.forEach((seg) => {
        const statusKey = (seg.activity || seg.assetStatus || "").toLowerCase().replace(/\s+/g, "");
        if (statusKey === "charging" || statusKey === "ac_charging" || statusKey === "dc_charging") {
            chargingSessions += 1;
        }
        else if (statusKey === "discharging" || statusKey === "working") {
            dischargingSessions += 1;
        }
        const chargeVal = seg.chargeEnergy;
        if (typeof chargeVal === "number" && Number.isFinite(chargeVal)) {
            activityChargeEnergy += chargeVal;
            hasActivityEnergy = true;
        }
        const dischargeVal = seg.dischargeEnergy;
        if (typeof dischargeVal === "number" && Number.isFinite(dischargeVal)) {
            activityDischargeEnergy += dischargeVal;
            hasActivityEnergy = true;
        }
    });
    return {
        activityChargeEnergy,
        activityDischargeEnergy,
        chargingSessions,
        dischargingSessions,
        hasActivityEnergy
    };
};
/** Resolve the display values + loading/error state for the Energy In/Out cards,
 *  preferring activity-derived energy (populated today) and falling back to the
 *  statistics energy totals when activity segments carry no per-segment kWh. */
const resolveEnergyCardState = (inputs) => {
    const hasEnergyDisplay = inputs.hasActivityEnergy || inputs.hasEnergyData;
    return {
        chargeEnergyDisplay: inputs.hasActivityEnergy ? inputs.activityChargeEnergy : inputs.totalCharged,
        chargingSessionsLabel: inputs.hasActivityData ? String(inputs.chargingSessions) : MISSING_VALUE,
        dischargeEnergyDisplay: inputs.hasActivityEnergy ? inputs.activityDischargeEnergy : inputs.totalDischarged,
        dischargingSessionsLabel: inputs.hasActivityData ? String(inputs.dischargingSessions) : MISSING_VALUE,
        energyCardsError: (inputs.isEnergyError || inputs.isActivityError) && !hasEnergyDisplay,
        energyCardsLoading: (inputs.isEnergyLoading || inputs.isActivityLoading) && !hasEnergyDisplay,
        hasEnergyDisplay
    };
};
/**
 * Range-total energy KPI card ("Energy In" / "Energy Out" over the selected period).
 * Two-column layout per the designer mockup: the title and prominent kWh total (with a
 * short yellow accent underline) sit on the left, while the session count and total
 * duration are right-aligned on the right. kWh and duration come from the statistics
 * API; the session count has no ESS backend field yet, so it renders as "--"
 * instead of a fabricated number.
 */
const EnergySummaryCard = ({ durationLabel, durationTestId, durationTitle, emptyTestId, hasData, isError, isLoading, loadingTestId, sessionsLabel, sessionsTestId, sessionsTitle, title, value, valueTestId, widgetTestId }) => {
    let valueBlock;
    if (isLoading) {
        valueBlock = (_jsx("div", { className: "flex items-center h-12", "data-test": loadingTestId, children: _jsx(CatLoadingIndicator, {}) }));
    }
    else if (!isError && hasData) {
        valueBlock = (_jsx("div", { className: "flex flex-col items-start", children: _jsxs("div", { className: "inline-flex items-baseline border-b-[3px] border-[#FFCD11] pb-1", "data-test": valueTestId, children: [_jsx("span", { className: "[font-family:'Roboto_Condensed',Arial,sans-serif] text-[32px] leading-[40px] font-bold text-[#000000]", children: formatEnergyValue(value) }), _jsx("span", { className: "[font-family:'Noto_Sans',sans-serif] text-[16px] leading-[24px] font-semibold text-[#000000] ml-1", children: "kWh" })] }) }));
    }
    else {
        valueBlock = (_jsx("div", { className: "flex flex-col items-start", "data-test": emptyTestId, children: _jsxs("div", { className: "inline-flex items-baseline border-b-[3px] border-[#FFCD11] pb-1", children: [_jsx("span", { className: "[font-family:'Roboto_Condensed',Arial,sans-serif] text-[32px] leading-[40px] font-bold text-[#000000]", children: "--" }), _jsx("span", { className: "[font-family:'Noto_Sans',sans-serif] text-[16px] leading-[24px] font-semibold text-[#000000] ml-1", children: "kWh" })] }) }));
    }
    return (_jsx(CatCard, { "data-test": widgetTestId, style: { "--cat-card-border-color": "#CCCCCC" }, children: _jsxs("div", { className: "flex items-start justify-between gap-4", children: [_jsxs("div", { className: "flex flex-col items-start", children: [_jsx("span", { className: "[font-family:'Roboto_Condensed',Arial,sans-serif] text-[20px] leading-[28px] font-bold text-center text-[#3F3F3F]", children: title }), _jsx("div", { className: "mt-6", children: valueBlock })] }), _jsxs("div", { className: "flex flex-col items-end text-right", children: [_jsxs("div", { className: "flex flex-col items-end", "data-test": sessionsTestId, children: [_jsx("span", { className: "[font-family:'Noto_Sans',sans-serif] text-[12px] leading-[16px] font-normal text-[#5C656C]", children: sessionsTitle }), _jsx("span", { className: "[font-family:'Noto_Sans',sans-serif] text-[14px] leading-[20px] font-bold text-right text-[#393D41] mt-0.5", children: sessionsLabel })] }), _jsxs("div", { className: "flex flex-col items-end mt-4", "data-test": durationTestId, children: [_jsx("span", { className: "[font-family:'Noto_Sans',sans-serif] text-[12px] leading-[16px] font-normal text-[#5C656C]", children: durationTitle }), _jsx("span", { className: "[font-family:'Noto_Sans',sans-serif] text-[14px] leading-[20px] font-bold text-right text-[#393D41] mt-0.5", children: durationLabel })] })] })] }) }));
};
/**
 * ESS Asset Detail Drawer — Historical Info section (Story US3, WI 2910595).
 *
 * Renders, over a user-selected date range, the historical energy summary cards
 * (Total Charged / Total Discharged), the charge/discharge activity log, and the
 * Alerts section (US5, WI 2910593) beneath them. The SOC/Power & Energy-use
 * history charts (US4) remain out of scope here. The date range is resolved in the
 * project's timezone and fed to the existing range-capable asset statistics/events
 * endpoints.
 */
const EssHistoricalInfo = ({ assetDrawerActive, assetId, projectId, projectTimezone, timezoneOffset }) => {
    const { customFromDate, customToDate, fromDateISO, handleCustomDateApply, handleCustomDateCancel, handleDateRangeChange, selectedDateRange, showCustomDatePicker, toDateISO } = useHistoricalDateRange({
        assetDrawerActive,
        projectTimezone,
        timezoneOffset
    });
    // Alerts (US5): the shared Alerts component provides the list + pagination; the
    // parent owns the sort control and the 24h count badge (mirrors the charger drawer).
    const { projectName } = useProjectDetailsStore((state) => ({
        projectName: state.projectDetails?.projectName
    }));
    const [alertsSortOrder, setAlertsSortOrder] = useState("DESC");
    const [alerts24hCount, setAlerts24hCount] = useState(0);
    const canFetch = Boolean(assetDrawerActive && assetId && projectId && fromDateISO && toDateISO);
    // --- Energy summary (range totals) ---------------------------------
    const { data: energyStats, isError: isEnergyError, isPending: isEnergyLoading } = useMethanolStatisticsQuery({
        assetId,
        enabled: canFetch,
        fromDate: fromDateISO,
        projectId,
        statType: "energy",
        toDate: toDateISO
    });
    const { hasEnergyData, totalCharged, totalDischarged } = useMemo(() => {
        const details = energyStats?.statDetails ?? [];
        let charge = 0;
        let discharge = 0;
        let hasData = false;
        details.forEach((detail) => {
            const energyDetails = detail.energyDetails;
            if (!energyDetails)
                return;
            const chargeValue = energyDetails.chargeEnergy?.value;
            const dischargeValue = energyDetails.dischargeEnergy?.value;
            if (typeof chargeValue === "number" && Number.isFinite(chargeValue)) {
                charge += chargeValue;
                hasData = true;
            }
            if (typeof dischargeValue === "number" && Number.isFinite(dischargeValue)) {
                discharge += dischargeValue;
                hasData = true;
            }
        });
        return { hasEnergyData: hasData, totalCharged: charge, totalDischarged: discharge };
    }, [energyStats]);
    // --- ESS activity segments (real backend data, per-day fan-out) ----
    // Timeline AND Activity History are both built from the project asset events
    // endpoint's per-day variant (`?startDate=` + assetId filter) — the only
    // variant the backend populates for ESS. The timeRange-events and
    // runningHours-statistics variants return empty for ESS. We fan out one
    // request per day across the range and merge; the query keys match the SoC
    // chart's, so React Query dedupes and no duplicate calls are made.
    const activityDays = useMemo(() => toDayList(fromDateISO, toDateISO), [fromDateISO, toDateISO]);
    const activityResults = useQueries({
        queries: activityDays.map((day) => ({
            enabled: canFetch,
            queryFn: () => getProjectAssetChargeActivitiesDataWithAssetId(projectId, day, assetId),
            queryKey: ["essDayActivities", projectId, assetId, day],
            retry: false,
            staleTime: ACTIVITY_STALE_TIME_MS
        }))
    });
    const mergedActivities = useMemo(() => {
        const collected = [];
        activityResults.forEach((result) => {
            const acts = result.data?.assetChargeActivities ??
                [];
            acts.forEach((seg) => {
                if (seg.activityStartTimeStamp)
                    collected.push(seg);
            });
        });
        // Most-recent-first for the Timeline list.
        collected.sort((first, second) => new Date(second.activityStartTimeStamp ?? 0).getTime() -
            new Date(first.activityStartTimeStamp ?? 0).getTime());
        return collected;
    }, [activityResults]);
    const isActivityLoading = canFetch && activityDays.length > 0 && activityResults.some((result) => result.isLoading);
    const isActivityError = canFetch &&
        !isActivityLoading &&
        activityResults.length > 0 &&
        activityResults.every((result) => result.isError);
    const hasActivityData = mergedActivities.length > 0;
    // Expand each activity into narrative Timeline entries (per Figma): a
    // charge/discharge segment becomes two rows — "…started at {startSoc}%" at
    // its start and "…stopped at {endSoc}%" at its end — while idle/other statuses
    // stay a single row. Entries are re-sorted most-recent-first for display.
    const timelineEntries = useMemo(() => {
        const entries = [];
        mergedActivities.forEach((seg, segIndex) => {
            const statusKey = normaliseStatusKey(seg.activity || seg.assetStatus);
            const label = ESS_STATUS_CONFIG[statusKey]?.label || seg.activity || seg.assetStatus || MISSING_VALUE;
            const baseKey = `${seg.assetId ?? "event"}-${segIndex}`;
            const isChargeLike = statusKey === "charging" || statusKey === "discharging";
            if (isChargeLike) {
                entries.push({
                    description: `${label} started${formatSocSuffix(seg.startSoc)}`,
                    key: `${baseKey}-start`,
                    timestamp: seg.activityStartTimeStamp
                });
                if (seg.activityEndTimeStamp) {
                    entries.push({
                        description: `${label} stopped${formatSocSuffix(seg.endSoc)}`,
                        key: `${baseKey}-stop`,
                        timestamp: seg.activityEndTimeStamp
                    });
                }
            }
            else {
                entries.push({
                    description: label,
                    key: `${baseKey}-status`,
                    timestamp: seg.activityStartTimeStamp
                });
            }
        });
        entries.sort((first, second) => new Date(second.timestamp ?? 0).getTime() - new Date(first.timestamp ?? 0).getTime());
        return entries;
    }, [mergedActivities]);
    // Activity History donut + Energy card durations, derived from the same real
    // per-day segments (per-segment duration = end − start, grouped by status).
    const { chargingDuration, dischargingDuration, statusMetrics } = useMemo(() => {
        const statusHistory = mergedActivities.map((seg) => ({
            assetStatus: seg.activity || seg.assetStatus || "",
            duration: hoursBetween(seg.activityStartTimeStamp, seg.activityEndTimeStamp)
        }));
        const grouped = groupStatusHistory(statusHistory);
        const metrics = grouped
            .map((entry) => {
            const config = ESS_STATUS_CONFIG[entry.assetStatus];
            return config
                ? {
                    color: config.color,
                    infoTooltip: config.tooltip,
                    key: entry.assetStatus,
                    label: config.label,
                    value: entry.duration
                }
                : null;
        })
            .filter((metric) => metric !== null);
        const findDuration = (status) => grouped.find((entry) => entry.assetStatus === status)?.duration ?? 0;
        return {
            chargingDuration: findDuration("charging"),
            dischargingDuration: findDuration("discharging"),
            statusMetrics: metrics
        };
    }, [mergedActivities]);
    // --- Energy totals + session counts from activity segments -----------
    // The per-day activity segments already carry per-segment chargeEnergy /
    // dischargeEnergy (kWh) and each charge/discharge segment is one session,
    // so real kWh totals and session counts are derived here directly from the
    // segments that already power the durations — no dedicated aggregate
    // endpoint needed (the /assets/statistics energy variant returns empty for
    // ESS today). Falls back to the statistics energy totals if activity
    // segments carry no per-segment energy.
    const { activityChargeEnergy, activityDischargeEnergy, chargingSessions, dischargingSessions, hasActivityEnergy } = useMemo(() => deriveActivityEnergyTotals(mergedActivities), [mergedActivities]);
    // Prefer activity-derived energy (populated today); fall back to the
    // statistics energy totals when activity segments carry no per-segment kWh.
    const { chargeEnergyDisplay, chargingSessionsLabel, dischargeEnergyDisplay, dischargingSessionsLabel, energyCardsError, energyCardsLoading, hasEnergyDisplay } = resolveEnergyCardState({
        activityChargeEnergy,
        activityDischargeEnergy,
        chargingSessions,
        dischargingSessions,
        hasActivityData,
        hasActivityEnergy,
        hasEnergyData,
        isActivityError,
        isActivityLoading,
        isEnergyError,
        isEnergyLoading,
        totalCharged,
        totalDischarged
    });
    // --- Activity log (client-side pagination over the merged range) ---
    const [visibleActivityCount, setVisibleActivityCount] = useState(TIMELINE_INITIAL_COUNT);
    useEffect(() => {
        setVisibleActivityCount(TIMELINE_INITIAL_COUNT);
    }, [fromDateISO, toDateISO, assetId, projectId]);
    const visibleActivities = useMemo(() => timelineEntries.slice(0, visibleActivityCount), [timelineEntries, visibleActivityCount]);
    const hasMoreActivities = visibleActivityCount < timelineEntries.length;
    const handleShowMore = useCallback(() => {
        setVisibleActivityCount((prev) => prev + TIMELINE_INITIAL_COUNT);
    }, []);
    let activityBody;
    if (isActivityLoading) {
        activityBody = (_jsx("div", { className: "flex flex-1 items-center justify-center min-h-[280px] py-8", "data-test": "ess-historical-activity-loading", children: _jsx(CatLoadingIndicator, {}) }));
    }
    else if (isActivityError) {
        activityBody = (_jsx("div", { className: "flex flex-1 items-center justify-center min-h-[280px] py-8", "data-test": "ess-historical-activity-error", children: _jsx("span", { className: "text-gray-500 text-sm", children: "Failed to load activity." }) }));
    }
    else if (!hasActivityData) {
        activityBody = (_jsx("div", { className: "flex flex-1 items-center justify-center min-h-[280px]", "data-test": "ess-historical-activity-empty", children: _jsx(EssHistoricalEmptyState, { description: "No activity was recorded for this asset in the selected range.", title: "No Data Available" }) }));
    }
    else {
        activityBody = (_jsxs(_Fragment, { children: [_jsxs("div", { className: "relative px-4 pb-4 pt-4", "data-test": "ess-historical-activity-list", children: [_jsx("div", { "aria-hidden": "true", className: "absolute bottom-2 left-[22px] top-5 w-[3px] -translate-x-1/2 bg-[#3F3F3F] [-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_80%,transparent_100%)] [mask-image:linear-gradient(to_bottom,black_0%,black_80%,transparent_100%)]", "data-test": "ess-historical-activity-rail" }), visibleActivities.map((event, index) => {
                            const description = event.description;
                            return (_jsxs("div", { className: "relative flex pb-6", "data-test": "ess-historical-activity-item", children: [_jsx("div", { className: "relative z-10 mr-4 flex w-3 flex-shrink-0 justify-center", children: _jsx("div", { className: `mt-1 h-3 w-3 flex-shrink-0 rounded-full border-2 border-black ${index === 0 ? "bg-black" : "bg-white"}`, "data-test": "ess-historical-activity-item-marker", "data-test-is-latest": index === 0 }) }), _jsxs("div", { className: "grid flex-1 grid-cols-2 items-start", children: [_jsxs("div", { className: "flex flex-col space-y-0.5", children: [_jsx("span", { className: "[font-family:'Noto_Sans',sans-serif] text-[14px] leading-[20px] font-bold tracking-normal text-[#3F3F3F]", "data-test": "ess-historical-activity-item-heading", children: "Asset Status" }), _jsx("span", { className: "[font-family:'Noto_Sans',sans-serif] text-[12px] leading-[16px] font-normal tracking-normal text-[#3F3F3F]", "data-test": "ess-historical-activity-item-status", children: description })] }), _jsx("span", { className: "text-left text-xs font-normal text-[#666565] [font-family:'Noto_Sans',sans-serif]", "data-test": "ess-historical-activity-item-time", children: formatEventTimestamp(event.timestamp, projectTimezone) })] })] }, event.key));
                        })] }), hasMoreActivities && (_jsx("div", { className: "mt-auto pb-3.5 text-center", "data-test": "ess-historical-activity-show-more-wrapper", children: _jsx("button", { className: "[font-family:'Noto_Sans',sans-serif] text-[14px] leading-[20px] font-semibold tracking-normal text-center align-middle text-[#0067B8] hover:underline", "data-test": "ess-historical-activity-show-more", onClick: handleShowMore, type: "button", children: "Show More" }) }))] }));
    }
    return (_jsxs("div", { className: "flex flex-col w-full space-y-4", "data-test": "ess-historical-info", children: [_jsxs("div", { className: "flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3", "data-test": "ess-historical-info-header", children: [_jsx(CatHeading, { "data-test": "ess-historical-info-title", variant: "title", children: "Historical Info" }), _jsxs("div", { className: "relative flex items-center gap-4", "data-test": "ess-historical-date-range-wrapper", children: [_jsx(DateRangeDropdown, { "data-test": "ess-historical-date-range-dropdown", onValueChange: handleDateRangeChange, selectedValue: selectedDateRange }), _jsx(CustomDatePickerPopover, { initialEndDate: customToDate?.toISOString(), initialStartDate: customFromDate?.toISOString(), maxRangeDays: MAX_CUSTOM_DATE_RANGE_DAYS, onApply: handleCustomDateApply, onCancel: handleCustomDateCancel, show: showCustomDatePicker, timezoneOffset: timezoneOffset })] })] }), _jsxs("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-4", "data-test": "ess-historical-energy-summary", children: [_jsx(EnergySummaryCard, { durationLabel: formatDuration(chargingDuration), durationTestId: "ess-historical-total-charged-duration", durationTitle: "Total charging duration", emptyTestId: "ess-historical-total-charged-empty", hasData: hasEnergyDisplay, isError: energyCardsError, isLoading: energyCardsLoading, loadingTestId: "ess-historical-total-charged-loading", sessionsLabel: chargingSessionsLabel, sessionsTestId: "ess-historical-total-charged-sessions", sessionsTitle: "Number of charging sessions", title: "Energy In", value: chargeEnergyDisplay, valueTestId: "ess-historical-total-charged-value", widgetTestId: "ess-historical-total-charged-card" }), _jsx(EnergySummaryCard, { durationLabel: formatDuration(dischargingDuration), durationTestId: "ess-historical-total-discharged-duration", durationTitle: "Total discharging duration", emptyTestId: "ess-historical-total-discharged-empty", hasData: hasEnergyDisplay, isError: energyCardsError, isLoading: energyCardsLoading, loadingTestId: "ess-historical-total-discharged-loading", sessionsLabel: dischargingSessionsLabel, sessionsTestId: "ess-historical-total-discharged-sessions", sessionsTitle: "Number of discharging sessions", title: "Energy Out", value: dischargeEnergyDisplay, valueTestId: "ess-historical-total-discharged-value", widgetTestId: "ess-historical-total-discharged-card" })] }), _jsxs("div", { className: "grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch", "data-test": "ess-historical-activity-section", children: [_jsxs("div", { className: "flex flex-col h-full rounded border border-[#CCCCCC] bg-white", "data-test": "ess-historical-activity-log", children: [_jsx("div", { className: "px-4 pt-4 pb-4", children: _jsx("span", { className: "[font-family:'Roboto_Condensed',Arial,sans-serif] text-[20px] leading-[28px] font-bold tracking-normal text-[#3F3F3F]", "data-test": "ess-historical-activity-log-title", children: "Timeline" }) }), activityBody] }), _jsx("div", { "data-test": "ess-historical-status-breakdown", children: _jsx(EssChargeHistoryTotal, { emptyContent: _jsx(EssHistoricalEmptyState, { description: "No activity history is available for this asset in the selected range.", title: "No Data Available" }), error: isActivityError ? "Failed to load status history." : undefined, loading: isActivityLoading, metrics: statusMetrics }) })] }), _jsxs("div", { className: "min-h-[472px] rounded border border-[#CCCCCC] bg-white p-4", "data-test": "ess-alerts-section", children: [_jsxs("div", { className: "flex items-center gap-3 mb-4", "data-test": "ess-alerts-header", children: [_jsx("span", { className: "[font-family:'Roboto_Condensed',Arial,sans-serif] text-[20px] leading-[28px] font-bold text-[#393D41]", "data-test": "ess-alerts-title", children: "Alerts" }), _jsxs("span", { className: "inline-flex h-[27px] items-center gap-1 rounded-[16px] bg-[#FFCD11] px-[10.5px]", "data-test": "ess-alerts-header-count", children: [_jsx(CatIconWarningTriangleFilled, { className: "shrink-0 text-[#3F3F3F]", style: { "--cat-icon-height": "12px", "--cat-icon-width": "12px" } }), _jsxs("span", { className: "whitespace-nowrap [font-family:'Noto_Sans',sans-serif] text-[12px] leading-[13px] font-semibold text-[#000000]", children: [alerts24hCount, " New (24h)"] })] }), _jsx("div", { className: "ml-auto", "data-test": "ess-alerts-sort", children: _jsxs(CatContextualMenu, { "data-test": "ess-alerts-sort-menu", position: "bottom", children: [_jsx(CatButton, { slot: "trigger", variant: "ghost", children: _jsxs("span", { className: "flex items-center gap-1 [font-family:'Noto_Sans',sans-serif] text-[14px] leading-[20px] font-semibold text-[#3F3F3F]", children: [alertsSortOrder === "DESC" ? (_jsx(CatIconArrowDownAlt, { className: "h-4 w-4" })) : (_jsx(CatIconArrowUpAlt, { className: "h-4 w-4" })), "Sort", alertsSortOrder === "DESC" ? (_jsx(CatIconChevronDown, { className: "ml-4", style: CARET_ICON_SIZE_STYLE })) : (_jsx(CatIconChevronUp, { className: "ml-4", style: CARET_ICON_SIZE_STYLE }))] }) }), _jsxs(CatList, { children: [_jsx(CatListItem, { "data-test": "ess-alerts-sort-newest", isActive: alertsSortOrder === "DESC", onBlSelect: () => setAlertsSortOrder("DESC"), children: "Sort by Latest" }), _jsx(CatListItem, { "data-test": "ess-alerts-sort-earliest", isActive: alertsSortOrder === "ASC", onBlSelect: () => setAlertsSortOrder("ASC"), children: "Sort by Earliest" })] })] }) })] }), _jsx(Alerts, { assetId: assetId ?? "", assetSourceType: "ess", isDataAvailableForAsset: () => undefined, isRefreshComponent: canFetch, last24hrsAlertsCount: (count) => setAlerts24hCount(count), offset: timezoneOffset, onSortOrderChange: (order) => setAlertsSortOrder(order), projectId: projectId ?? "", projectName: projectName || "--", sortOrder: alertsSortOrder, timezone: projectTimezone ?? "" })] })] }));
};
export default EssHistoricalInfo;
